import { MasterType } from '@/common/model/master/w2Master'
import type { ConversionAttribution, ConversionMetrics } from '@/common/model/recommend/conversion'
import { WidgetDisplayLogic } from '@/common/model/recommend/widget'
import { Site, SiteW2Version } from '@/common/model/site'

export const SiteApiPath = {
  CREATE_SITE: '/api/sites', // POST Create Site
  LIST_SITES: '/api/sites', // GET List Sites
  GET_SITE: '/api/sites/{site_id}', // GET Get Site
  UPDATE_SITE: '/api/sites/{site_id}', // PUT Update Site
  REGENERATE: '/api/sites/{site_id}/regenerate-key', // POST Regenerate Api Key
  SITE_DASHBOARD: '/api/sites/{site_id}/dashboard', // GET Site Dashboard
  DELIVERY_STATS: '/api/sites/{site_id}/delivery-stats', // GET Delivery Stats
  PLACEMENT_STATS: '/api/sites/{site_id}/placement-stats', // GET Placement Stats
  GET_MODEL_TUNING: '/api/sites/{site_id}/model-tuning', // GET Get Model Tuning
  UPDATE_MODEL_TUNING: '/api/sites/{site_id}/model-tuning', // PUT Update Model Tuning
  GET_MASTER_SUMMARY: '/api/sites/{site_id}/master-summary', // GET Get Master Summary
  GET_EMBED_TAGS: '/api/sites/{site_id}/tags', // GET Get Embed Tags
  GET_SITE_CONFIG: '/api/site-config/{distribution_code}' // GET Get Site Config
}

export type SiteApiPath = (typeof SiteApiPath)[keyof typeof SiteApiPath]

export interface CreateSiteVars {
  name: string
  domain: string
  project_number?: string | null
  distribution_code?: string | null
  enable_full_behavior?: boolean | null
  behavior_analysis_limit?: number | null
  rec_sampling_rate?: number | null
  w2_version?: SiteW2Version | null
  // 特集ページ画像の取得バッチ（V5区分かつMedia-DRIVER有効のサイトのみ）
  feature_page_image_batch_enabled?: boolean | null
  feature_page_image_batch_hour?: number | null
  enable_content_analysis?: boolean | null
  enable_geo_llmo?: boolean | null
  enable_carte?: boolean | null
  enable_reco_media?: boolean | null
  enable_review?: boolean | null
  enable_ai_purchase_assist?: boolean | null
  enable_search_suggest?: boolean | null
  enable_tag_pages?: boolean | null
  enable_mail_reco?: boolean | null
  enable_article_upload?: boolean | null
  is_test?: boolean | null
}

export interface ListSitesVars {}

export interface GetSiteVars {
  site_id: number
}

export interface UpdateSiteVars {
  site_id: number
  name?: string | null
  domain?: string | null
  project_number?: string | null
  is_active?: boolean | null
  distribution_code?: string | null
  enable_full_behavior?: boolean | null
  behavior_analysis_limit?: number | null
  rec_sampling_rate?: number | null
  w2_version?: SiteW2Version | null
  // 特集ページ画像の取得バッチ（V5区分かつMedia-DRIVER有効のサイトのみ）
  feature_page_image_batch_enabled?: boolean | null
  feature_page_image_batch_hour?: number | null
  enable_content_analysis?: boolean | null
  enable_geo_llmo?: boolean | null
  enable_carte?: boolean | null
  enable_reco_media?: boolean | null
  enable_review?: boolean | null
  enable_ai_purchase_assist?: boolean | null
  enable_search_suggest?: boolean | null
  enable_tag_pages?: boolean | null
  enable_mail_reco?: boolean | null
  enable_article_upload?: boolean | null
  is_test?: boolean | null
  // --- API連携（W2 OpenAPI 送信）---
  enable_w2_send?: boolean | null
  enable_target_list?: boolean | null
  w2_api_key?: string | null
  w2_api_base_url?: string | null
  w2_basic_user?: string | null
  w2_basic_pass?: string | null
  w2_rate_limit_per_min?: number | null
  w2_retry_interval_sec?: number | null
  w2_retry_max?: number | null
  w2_auto_send_enabled?: boolean | null
  w2_auto_send_start_hour?: number | null
  w2_auto_send_interval_hours?: number | null
}

export interface RegenerateVars {
  site_id: number
}

export interface SiteDashboardVars {
  site_id: number
  // YYYY-MM
  month?: string
  // セットアップガイド専用の重い集計（W2マスタ種別ごとの件数・レコメンド配信数・
  // 行動タイプ別件数・マッピング件数）を含めるか。既定では集計されず 0 / 空で返る
  include_setup?: boolean
}

export interface DeliveryStatsVars {
  site_id: number
  // YYYY-MM
  month?: string
  days?: number
}

export interface PlacementStatsVars {
  site_id: number
  // YYYY-MM
  month?: string
  days?: number
  // type=ページ種別の一覧 / url=URL別（ドリルダウン）
  group?: 'type' | 'url'
  // group=url のときの絞り込み
  host?: string
  path_prefix?: string
  // group=url のとき、クエリ文字列まで分けて数えるか
  include_query?: boolean
}

export interface GetModelTuningVars {
  site_id: number
}

export interface UpdateModelTuningVars {
  site_id: number
  hybrid_weights: Record<string, unknown>
  collaborative: Record<string, unknown>
  neural: Record<string, unknown>
  content_based: Record<string, unknown>
}

export interface GetMasterSummaryVars {
  site_id: number
}

export interface GetEmbedTagsVars {
  site_id: number
}

export interface GetSiteConfigVars {
  distribution_code: string
}

export type ListSitesResponse = Site[]
export type CreateSiteResponse = Site
export type GetSiteResponse = Site
export type UpdateSiteResponse = Site

export interface RegenerateResponse {
  api_key: string
}

type ContentBasedResult =
  | { status: 'trained'; n_products: number; n_features: number }
  | { status: 'no_data'; n_products: undefined; n_features: undefined }
  | { status: 'error'; message: string; n_products: undefined; n_features: undefined }

type CollaborativeResult =
  | { status: 'trained'; rmse: number; n_ratings: number }
  | { status: 'no_data'; rmse: undefined; n_ratings: undefined }
  | { status: 'error'; message: string; rmse: undefined; n_ratings: undefined }

type NeuralResult =
  | { status: 'trained'; loss: number; accuracy: number; n_interactions: number }
  | { status: 'no_data'; loss: undefined; accuracy: undefined; n_interactions: undefined }
  | {
      status: 'error'
      message: string
      loss: undefined
      accuracy: undefined
      n_interactions: undefined
    }

type MemoryLog = {
  start: number
  after_content_based: number
  after_collaborative: number
  after_neural: number
  peak: number
}

interface RetrainResultDetail {
  content_based?: ContentBasedResult
  collaborative?: CollaborativeResult
  neural?: NeuralResult
  memory_mb?: MemoryLog // バックグラウンド実行時のみ存在
  // エラー時
  error?: string
}

export interface SiteDashboardResponse {
  site_name: string
  month: string
  month_label: string
  products: number
  categories: number
  item_category_mappings: number
  orders: number
  inventory: number
  features: number
  widgets: number
  accounts: number
  retrain_last_at: string | null
  retrain_status: 'complete' | 'error' | null
  retrain_detail: RetrainResultDetail | null
  events_count: number
  identities_count: number
  orders_count: number
  customers_total: number
  customers_member: number
  customers_unknown: number
  daily_behavior: { date: string; count: number }[]
  daily_orders: { date: string; count: number }[]
  prev_behavior: { date: string; count: number }[]
  prev_orders: { date: string; count: number }[]
  today_behavior: number
  today_orders: number
  month_behavior: number
  month_page_views: number
  month_orders: number
  month_orders_total: number
  // 金額シェア率の分母（当月の全注文金額）
  month_amount_total: number
  // レコメンド経由の受注をコンバージョン種別ごとに持つ。合算せず、画面のトグルで切り替える
  recommend_orders: Record<
    ConversionAttribution,
    {
      today: number
      month: number
      month_amount: number
      daily: { date: string; count: number }[]
      prev_daily: { date: string; count: number }[]
    }
  >
  behavior_by_type: Record<string, number>
  recommendation_logs: number
  w2_master_counts?: Record<string, number>
}

export interface AbBreakdown extends ConversionMetrics {
  group: string
  impressions: number
  clicks: number
  conversions: number
  revenue: number
  ctr: number
  cvr: number
}

export interface WidgetDeliveryStat extends ConversionMetrics {
  widget_id: number | null
  widget_name: string
  display_logic: WidgetDisplayLogic
  // 記事パーツと商品パーツを見分けるために使う（display_logic から推測しない）
  content_type: string | null
  impressions: number
  clicks: number
  conversions: number
  revenue: number
  ctr: number
  cvr: number
  direct_cvr: number
  assist_cvr: number
  avg_dwell_ms: number
  total_dwell_30s_rate: number
  ab_breakdown: AbBreakdown[]
}

export interface DeliveryStatsResponse {
  total_impressions: number
  total_clicks: number
  total_conversions: number
  total_revenue: number
  total_ctr: number
  total_cvr: number
  total_direct_conversions: number
  total_direct_revenue: number
  total_assist_conversions: number
  total_assist_revenue: number
  // 複数のレコメンドパーツへまたがって計上された注文の件数。
  // パーツ別の数字を足し上げてもサマリーにならない理由を、画面で実数として示すために使う
  multi_widget_orders_direct: number
  multi_widget_orders_assist: number
  total_avg_dwell_ms: number | null
  total_dwell_30s_rate: number | null
  widgets: WidgetDeliveryStat[]
  daily: {
    date: string
    impressions: number
    clicks: number
    conversions: number
    direct_conversions: number
    direct_revenue: number
    assist_conversions: number
    assist_revenue: number
  }[]
  // 集計範囲（YYYY-MM-DD）
  since: string
  until: string
  period_days: number
}

export interface GetModelTuningResponse {
  hybrid_weights: Record<string, number>
  collaborative: Record<string, unknown>
  neural: Record<string, unknown>
  content_based: Record<string, unknown>
}

export type UpdateModelTuningResponse = GetModelTuningResponse

export interface MasterSummaryItem {
  master_type: MasterType
  count: number
  active_count: number | null
  last_updated_at: string | null
  last_upload_at: string | null
  upload_source: string | null
}

export interface GetMasterSummaryResponse {
  masters: MasterSummaryItem[]
  latest_upload: string | null
}

export interface GetEmbedTagsVars {
  site_id: number
}

/** 配信ごとのサイト設置用タグ（backend schemas/widget.py WidgetTagOut） */
export interface EmbedWidgetTag {
  code: string
  name: string
  display_logic: string
  display_logic_label: string
  tag: string
  needs_item_id: boolean
  needs_feature_page_id: boolean
}

/** メール用タグの差し込み変数の説明（backend schemas/widget.py MailTagPlaceholderOut） */
export interface EmbedMailTagPlaceholder {
  /** URL のクエリ名（r=会員ID / o=受注ID / i=商品ID / k=配信キー） */
  key: string
  /** タグ内の目印の文字。空はタグに目印を置かない任意の項目 */
  placeholder: string
  label: string
  description: string
}

/** 配信ごとのメール用タグ（backend schemas/widget.py MailTagOut） */
export interface EmbedMailTag {
  code: string
  name: string
  display_logic: string
  display_logic_label: string
  /** recipient=受信者起点 / item=購入商品起点 */
  origin: string
  /** メール本文に貼る HTML */
  tag: string
  placeholders: EmbedMailTagPlaceholder[]
  /** 見本の HTML（tag と同じ内容で、URL を管理画面と同じ配信元を指す相対URLにしたもの） */
  preview_html: string
  /** 商品の枠の数 */
  slots: number
  /** 1行の枠の数 */
  columns: number
}

// 2026-09-13 修正: 実際の応答（backend schemas/widget.py EmbedTagsOut）と食い違っていたため合わせる
export interface GetEmbedTagsResponse {
  site_id: number
  site_name: string
  distribution_code: string | null
  enable_full_behavior: boolean
  rec_master_mode: string
  w2sr_base_tag: string
  w2sr_view_product_tag: string
  w2sr_view_category_tag: string
  w2sr_view_feature_tag: string
  w2sr_view_feature_category_tag: string
  w2sr_cart_tag: string
  w2sr_purchase_tag: string
  w2sr_common_recommend_tag: string
  w2sr_product_recommend_tag: string
  w2sr_cart_recommend_tag: string
  w2sr_purchase_recommend_tag: string
  w2sr_mypage_recommend_tag: string
  w2sr_category_recommend_tag: string
  w2sr_widget_tags: EmbedWidgetTag[]
  w2sr_mail_tags: EmbedMailTag[]
  // 2026-10-06 追加: メールレコメンドを使えるか（サイトの enable_mail_reco かつ mail_reco の権限）
  mail_reco_enabled: boolean
}

export interface GetSiteConfigResponse {
  distribution_code: string
  api_key: string
  enable_full_behavior: boolean
  rec_sampling_rate: number
  enable_content_analysis: boolean
  enable_geo_llmo: boolean
  enable_carte: boolean
  enable_reco_media: boolean
  enable_review: boolean
  enable_ai_purchase_assist: boolean
  enable_article_upload: boolean
}

// 設置場所（ページ）別の配信実績。openspec: recommend-placement-cv
export interface PlacementStat extends ConversionMetrics {
  placement_host: string | null
  placement_path: string | null
  placement_query: string | null
  // 画面に出す表示名。メールは「メール: 識別子」、計測前は「(計測前)」
  label: string
  // 記録が始まる前の実績。他の設置場所へ混ぜず、1行にまとめて示す
  is_unmeasured: boolean
  impressions: number
  clicks: number
  ctr: number
  direct_cvr: number
  assist_cvr: number
}

export interface PlacementStatsResponse {
  total_impressions: number
  total_clicks: number
  total_direct_conversions: number
  total_direct_revenue: number
  total_assist_conversions: number
  total_assist_revenue: number
  placements: PlacementStat[]
  group: string
  since: string
  until: string
  period_days: number
}
